import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ExternalLink, Building2 } from "lucide-react";

/**
 * "We proudly represent" — visible to all roles. Renders the active agency's
 * roster of active manufacturer lines with a website link per row.
 *
 * variant:
 *   - "card"    (default) — light card, used inline on full pages.
 *   - "sidebar" — compact dark version, used inside the navigation drawer.
 */
export default function WeProudlyRepresent({
  agencyId,
  agencyName,
  variant = "card",
  onLinkClick,
}) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!agencyId) {
      setLines([]);
      return;
    }
    setLoading(true);
    api
      .get(`/rep-agencies/${agencyId}/lines`, {
        params: { include_inactive: false },
      })
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : [];
        const filtered = data
          .filter((ln) => ln.manufacturer && ln.active !== false)
          .sort((a, b) =>
            (a.manufacturer.name || "").localeCompare(b.manufacturer.name || "")
          );
        setLines(filtered);
      })
      .catch(() => {
        if (!cancelled) setLines([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  if (!agencyId) return null;
  if (!loading && lines.length === 0) return null;

  const normalizeUrl = (url) => {
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const stripScheme = (url) =>
    (url || "").replace(/^https?:\/\//i, "").replace(/\/$/, "");

  if (variant === "sidebar") {
    return (
      <section
        className="px-3 py-4 border-t border-slate-800"
        data-testid="we-proudly-represent"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono-data px-1">
          <Building2 className="w-3 h-3" />
          We proudly represent
        </div>
        <ul
          className="mt-3 space-y-2.5 max-h-[40vh] overflow-y-auto pr-1"
          data-testid="we-proudly-represent-list"
        >
          {lines.map((ln) => {
            const m = ln.manufacturer;
            const href = normalizeUrl(m.website);
            return (
              <li
                key={ln.line_id || m.id}
                className="px-1 py-1.5 rounded-sm hover:bg-slate-800/60"
                data-testid={`we-rep-row-${m.id}`}
              >
                <div className="text-sm font-medium text-slate-200 leading-snug">
                  {m.name}
                </div>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onLinkClick}
                    className="text-[11px] text-slate-500 hover:text-amber-400 inline-flex items-center gap-1 mt-0.5 font-mono-data break-all"
                    data-testid={`we-rep-link-${m.id}`}
                  >
                    {stripScheme(href)}
                    <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                  </a>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  // Default "card" variant
  return (
    <section
      className="mt-6 bg-white border border-border rounded-sm p-5"
      data-testid="we-proudly-represent"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
        <Building2 className="w-3.5 h-3.5" />
        We proudly represent
        {agencyName ? (
          <span className="text-slate-400 normal-case tracking-normal">
            · {agencyName}
          </span>
        ) : null}
      </div>

      <ul className="mt-4 divide-y divide-border">
        {lines.map((ln) => {
          const m = ln.manufacturer;
          const href = normalizeUrl(m.website);
          return (
            <li
              key={ln.line_id || m.id}
              className="py-3 flex items-center justify-between gap-3"
              data-testid={`we-rep-row-${m.id}`}
            >
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">
                  {m.name}
                </div>
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-amber-600 inline-flex items-center gap-1 mt-0.5 font-mono-data break-all"
                    data-testid={`we-rep-link-${m.id}`}
                  >
                    {stripScheme(href)}
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                ) : (
                  <div className="text-xs text-muted-foreground mt-0.5 italic">
                    No website on file
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
